import { Injectable, NgZone, inject } from '@angular/core';
import * as signalR from '@microsoft/signalr';
import { BehaviorSubject, Subject } from 'rxjs';
import { AuthService } from 'libs/auth/src/lib/auth.service';
import { environment } from './app-settings.service';

/** GEN_BILDIRIM'e yazılan bildirim (ServerEvents host'u BildirimIletici iletir). */
export interface BildirimOlay {
  tur: 'bildirim';
  bildirimKod: number;
  baslik?: string | null;
  metin?: string | null;
  formAd?: string | null;
  itemKod?: number | null;
  ses?: boolean;
}

/** Kullanıcının bir sayacı değişti (ör. görev atandı) — GetGorevSayac yeniden okunmalı. */
export interface SayacOlay {
  tur: 'sayac';
  gorevTipi?: string | null;
}

/** Kurumsal mesajlaşma: yeni mesaj geldi (ServerEvents giden kutusu GOR_MESAJ_ALICI'yı okur; GEN_BILDIRIM'e yazılmaz). */
export interface MesajOlay {
  tur: 'mesaj';
  mesajKod: number;
  konuKod: number;
  gonderen?: string | null;
  konu?: string | null;
  ekSayisi?: number;
  ses?: boolean;
}

/** Bir kullanıcının ilk bağlantısı açıldı / son bağlantısı kapandı. */
export interface KullaniciDurumOlay {
  kullaniciKod: number;
  cevrimici: boolean;
}

/**
 * Gerçek zamanlı bağlantı (SignalR, ServerEvents host'u /hub/notifications) — uygulama başına tek bağlantı.
 * Ana sayfa (dk-main) açılınca başlar, çıkışta durur. server-event.service.ts'in yerine (06.10.2026):
 *  - token her bağlanmada güncel okunur (silent-renew sonrası yeniden bağlanma eski token'la denenmiyordu),
 *  - WebSocket kilidi yok (WebSocket geçmeyen ağda SSE / long polling'e düşer),
 *  - kalıcı kopmada 60 sn'de bir yeniden dener, ping zamanlayıcısı temizlenir,
 *  - olaylar tipli Observable olarak dağıtılır (console.log yerine).
 */
@Injectable({ providedIn: 'root' })
export class RealtimeService {
  private auth = inject(AuthService);
  private zone = inject(NgZone);

  private baglanti?: signalR.HubConnection;
  private pingId?: ReturnType<typeof setInterval>;
  private yenidenId?: ReturnType<typeof setTimeout>;
  private calisiyor = false;

  /** Hub'a bağlıyken true; header bağlı değilken sayaçları daha sık yokluyor. */
  readonly bagli$ = new BehaviorSubject<boolean>(false);
  readonly bildirim$ = new Subject<BildirimOlay>();
  readonly sayacDegisti$ = new Subject<SayacOlay>();
  readonly kullaniciDurum$ = new Subject<KullaniciDurumOlay>();
  readonly mesaj$ = new Subject<MesajOlay>();

  baslat(): void {
    if (this.calisiyor || !environment.enableServerEvents || !environment.serverEventUrl) return;
    this.calisiyor = true;
    this.zone.runOutsideAngular(() => {
      this.baglanti = this.olustur();
      this.baglan();
    });
  }

  async durdur(): Promise<void> {
    this.calisiyor = false;
    this.zamanlayicilariTemizle();
    const b = this.baglanti;
    this.baglanti = undefined;
    this.bagliYap(false);
    try {
      await b?.stop();
    } catch {
      // kapanırken hata önemsiz
    }
  }

  private olustur(): signalR.HubConnection {
    let deviceId = localStorage.getItem('deviceId');
    if (!deviceId) {
      deviceId = crypto.randomUUID();
      localStorage.setItem('deviceId', deviceId);
    }
    const kok = environment.serverEventUrl.endsWith('/') ? environment.serverEventUrl : environment.serverEventUrl + '/';
    const b = new signalR.HubConnectionBuilder()
      .withUrl(`${kok}hub/notifications?did=${deviceId}`, {
        accessTokenFactory: () => this.auth.loggedUser?.access_token ?? '',
      })
      .withAutomaticReconnect([0, 2000, 5000, 10000, 30000])
      .configureLogging(signalR.LogLevel.None)
      .build();

    b.on('bildirim', (o: BildirimOlay) => this.zone.run(() => this.bildirim$.next(o)));
    b.on('sayacDegisti', (o: SayacOlay) => this.zone.run(() => this.sayacDegisti$.next(o)));
    b.on('kullaniciDurum', (o: KullaniciDurumOlay) => this.zone.run(() => this.kullaniciDurum$.next(o)));
    b.on('mesaj', (o: MesajOlay) => this.zone.run(() => this.mesaj$.next(o)));

    b.onreconnecting(() => this.bagliYap(false));
    b.onreconnected(() => this.bagliYap(true));
    // Otomatik yeniden bağlanma da tükendiyse (ya da sunucu kapattıysa) 60 sn'de bir baştan dene
    b.onclose(() => {
      this.bagliYap(false);
      this.pingDurdur();
      this.yenidenPlanla();
    });
    return b;
  }

  private async baglan(): Promise<void> {
    if (!this.calisiyor || !this.baglanti) return;
    if (!this.auth.loggedUser?.access_token) {
      this.yenidenPlanla();
      return;
    }
    try {
      await this.baglanti.start();
      this.bagliYap(true);
      this.pingBaslat();
    } catch {
      this.yenidenPlanla();
    }
  }

  private yenidenPlanla(): void {
    if (!this.calisiyor || this.yenidenId) return;
    this.yenidenId = setTimeout(() => {
      this.yenidenId = undefined;
      if (this.baglanti?.state === signalR.HubConnectionState.Disconnected) this.baglan();
    }, 60000);
  }

  /** Ping yalnız sunucudaki "son görülme"yi tazeler (DB'ye yazmaz). */
  private pingBaslat(): void {
    this.pingDurdur();
    this.pingId = setInterval(() => {
      if (this.baglanti?.state === signalR.HubConnectionState.Connected) this.baglanti.invoke('Ping').catch(() => {});
    }, 30000);
  }

  private pingDurdur(): void {
    if (this.pingId) clearInterval(this.pingId);
    this.pingId = undefined;
  }

  private zamanlayicilariTemizle(): void {
    this.pingDurdur();
    if (this.yenidenId) clearTimeout(this.yenidenId);
    this.yenidenId = undefined;
  }

  private bagliYap(v: boolean): void {
    if (this.bagli$.value !== v) this.zone.run(() => this.bagli$.next(v));
  }
}
